import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import HeroSection from "@/components/HeroSection";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Check } from "lucide-react";
import {
  Modal,
  ModalBody,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
} from "@/components/modal";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-media-query";
import { useNavigate } from "react-router-dom";

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/partners.json`)
      .then((res) => res.json())
      .then((data) => setPartners(data))
      .catch((err) => console.error("Error loading partners:", err));
  }, []);

  const cursorRef = useRef(null);
  const targetOpacityRef = useRef(0);
  const currentOpacityRef = useRef(0);
  const targetScaleRef = useRef(1);
  const currentScaleRef = useRef(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;

    let mouseX = 0;
    let mouseY = 0;
    let rafId;

    const lerp = (a, b, n) => a + (b - a) * n;

    const update = () => {
      if (cursorRef.current) {
        currentScaleRef.current = lerp(
          currentScaleRef.current,
          targetScaleRef.current,
          0.15,
        );

        currentOpacityRef.current = lerp(
          currentOpacityRef.current,
          targetOpacityRef.current,
          0.2,
        );

        cursorRef.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0)
         translate(-50%, -50%)
         scale(${currentScaleRef.current})`;

        cursorRef.current.style.opacity = currentOpacityRef.current;
      }

      rafId = requestAnimationFrame(update);
    };

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    window.addEventListener("mousemove", onMove);
    rafId = requestAnimationFrame(update);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const categories = ["All", ...new Set(partners.map((p) => p.category))];
  const filteredPartners =
    selectedCategory === "All"
      ? partners
      : partners.filter((p) => p.category === selectedCategory);
  const [open, setOpen] = React.useState(false);
  const [selectedPartner, setSelectedPartner] = React.useState(null);
  const navigate = useNavigate();

  const onCardClick = (partner) => {
    if (partner.solutionSlug) {
      navigate(`/partners/${partner.solutionSlug}`);
      return;
    }
    setSelectedPartner(partner);
    setOpen(true);
  };
  return (
    <>
      <SEO
        title="Value Added Distributor - Leading Cybersecurity Vendors | Gulf Digital"
        description="Gulf Digital partners with leading cybersecurity vendors including NSFOCUS, Ghangor Cloud, CyberRT, BARRACUDA Networks, and more to deliver best-in-class security solutions."
        url="/partners"
        ogTitle="Leading Cybersecurity Vendors | Cybersecurity Solutions in KSA"
        ogDescription="Gulf Digital partners with leading vendors to deliver Cybersecurity solutions in KSA, including NSFOCUS, CyberRT, BARRACUDA, and more."
        twitterTitle="Our Partners | Cybersecurity Services in KSA"
        twitterDescription="Discover the vendor ecosystem behind Gulf Digital's Cybersecurity services in KSA, including NSFOCUS, CyberRT, and BARRACUDA Networks."
      />
      <StructuredData type="Organization" data={{
        parentOrganization: {
          '@type': 'Organization',
          name: 'Gulf Digital',
          url: 'https://gulfdigital-me.com'
        }
      }} />

      <HeroSection
        eyebrow="Our Partners"
        title="Partnering with Industry Leaders"
        subtitle="We collaborate with the world's leading cybersecurity vendors to deliver best-in-class solutions tailored for the Middle East region."
        variant="minimal"
      />

      {/* Category Filter */}
      <section
        className="py-8 bg-warm-gray-50 border-b border-warm-gray-200"
        data-testid="category-filter-section"
      >
        <div className="container-custom">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-brand-maroon text-white shadow-lg"
                    : "bg-white text-foreground hover:bg-warm-gray-100 border border-warm-gray-200"
                }`}
                data-testid={`filter-${category.toLowerCase().replace(" ", "-")}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20 bg-white" data-testid="partners-grid-section">
        <div className="container-custom">
          {!isMobile && (
            <div
              ref={cursorRef}
              className="fixed left-0 top-0 w-6 h-6 rounded-full bg-brand-maroon/80
    pointer-events-none z-[9999]
    flex items-center justify-center text-white text-sm font-semibold"
            ></div>
          )}

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 cursor-none"
            onMouseMove={() => {
              if (!isMobile) targetOpacityRef.current = 1;
            }}
            onMouseLeave={() => {
              if (!isMobile) targetOpacityRef.current = 0;
            }}
          >
            {filteredPartners.map((partner, index) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                data-testid={`partner-card-${partner.id}`}
              >
                <Card
                  className="h-full border-2 border-warm-gray-200 hover:border-brand-maroon hover:shadow-soft-lg transition-all duration-300 cursor-none hover:cursor-none"
                  onMouseEnter={() => (targetScaleRef.current = 1.5)}
                  onMouseLeave={() => (targetScaleRef.current = 1)}
                  onClick={() => onCardClick(partner)}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-20 flex items-center">
                        <img
                          src={partner.logo}
                          alt={`${partner.name} - Cybersecurity services in Saudi Arabia`}
                          className="max-h-16 max-w-full object-contain"
                        />
                      </div>
                      {/* <Badge className="bg-brand-maroon/10 text-brand-maroon hover:bg-brand-maroon/20">
                        {partner.category}
                      </Badge> */}
                    </div>
                    <CardTitle className="text-xl">{partner.name}</CardTitle>
                    <CardDescription>{partner.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold text-sm mb-3">
                      Key Solutions:
                    </h4>
                    <ul className="space-y-2">
                      {partner.solutions.map((solution, idx) => (
                        <li
                          key={idx}
                          className="flex items-start space-x-2 text-sm"
                        >
                          <Check className="w-4 h-4 text-brand-maroon flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">
                            {solution}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Value */}
      <section
        className="py-20 bg-warm-gray-50"
        data-testid="partnership-value-section"
      >
        <div className="container-custom">
          <SectionHeading
            eyebrow="Partnership Value"
            title="Why We Partner with the Best"
            description="Our partnerships enable us to deliver comprehensive, proven solutions with world-class support."
            titleColor="foreground"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Certified Expertise",
                description:
                  "Our team holds advanced certifications from all major partners",
              },
              {
                title: "Regional Support",
                description: "Local presence backed by global vendor resources",
              },
              {
                title: "Best Practices",
                description:
                  "Implementation methodologies proven across thousands of deployments",
              },
              {
                title: "Continuous Innovation",
                description:
                  "Early access to new features and security innovations",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-brand-maroon/10 flex items-center justify-center mx-auto mb-4">
                  <div className="w-6 h-6 rounded-full bg-brand-maroon" />
                </div>
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-20 bg-white" data-testid="become-partner-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Interested in Partnering with Us?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're always looking to expand our partnership ecosystem with
              innovative security vendors.
            </p>
            <a
              href="mailto:Sales@gulfdigital-me.com?subject=Partnership Inquiry"
              className="inline-flex items-center px-8 py-3 bg-brand-maroon text-white rounded-lg font-medium hover:bg-brand-maroon-dark transition-colors"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      <CTASection />
      <Preview open={open} setOpen={setOpen} partner={selectedPartner} />
    </>
  );
};

function Preview({ open, setOpen, partner }) {
  if (!partner) return null;

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent className="max-w-5xl">
        <ModalHeader className="text-center items-center">
          <img
            src={partner.logo}
            alt={`${partner.name} - Cybersecurity solutions in KSA`}
            className="h-16 mx-auto mb-4 object-contain"
          />
          {/* <ModalTitle className="text-2xl">{partner.name}</ModalTitle> */}
          <ModalDescription className="max-w-2xl">
            {partner.description}
          </ModalDescription>
        </ModalHeader>

        <ModalBody className="space-y-8">
          {/* Overview */}
          <section>
            <h3 className="font-semibold text-lg mb-2">Overview</h3>
            <p className="text-sm text-muted-foreground">
              {partner.modalContent.overview}
            </p>
          </section>

          {/* Key Capabilities */}
          <section>
            <h3 className="font-semibold text-lg mb-2">Key Capabilities</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm list-disc pl-4">
              {partner.modalContent.keyCapabilities.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Business Value */}
          <section>
            <h3 className="font-semibold text-lg mb-2">Business Value</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm list-disc pl-4">
              {partner.modalContent.businessValue.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Industries */}
          <section>
            <h3 className="font-semibold text-lg mb-2">Industries Served</h3>
            <div className="flex flex-wrap gap-2">
              {partner.modalContent.industries.map((industry, i) => (
                <span
                  key={i}
                  className="px-3 py-1 text-xs rounded-full bg-muted"
                >
                  {industry}
                </span>
              ))}
            </div>
          </section>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default Partners;
